import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { StatusBadge } from '../components/StatusBadge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { useAuth } from '../contexts/AuthContext';
import { ArrowLeft, Loader2, Link2, FileText, Check, X, User, Calendar, ExternalLink, MessageSquare, Edit } from 'lucide-react';
import { toast } from 'sonner';
import { getCategoryLabel, getCountryName } from '../lib/constants';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CustomMappingDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const fromPending = searchParams.get('from') === 'pending';
  
  const [mapping, setMapping] = useState(null);
  const [linkedEF, setLinkedEF] = useState(null);
  const [efHistory, setEfHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [approvalDialog, setApprovalDialog] = useState({ open: false, action: null });
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const { canApprove } = useAuth();

  const backUrl = fromPending ? '/pending-approvals' : '/custom-mappings';

  const fetchMappingDetails = useCallback(async () => {
    try {
      // Use the specific endpoint to get the mapping by ID
      const response = await axios.get(`${API_URL}/custom-mappings/${id}`);
      const found = response.data;
      
      if (!found) {
        toast.error('Custom mapping not found');
        navigate('/custom-mappings');
        return;
      }
      
      setMapping(found);
      
      // Fetch linked emission factor details
      if (found.desired_emission_factor_id) {
        try {
          const efResponse = await axios.get(`${API_URL}/emission-factors/${found.desired_emission_factor_id}`);
          setLinkedEF(efResponse.data);
          
          // Fetch EF history for comments
          const historyResponse = await axios.get(`${API_URL}/emission-factors/${found.desired_emission_factor_id}/history`);
          setEfHistory(historyResponse.data.history || []);
        } catch (error) {
          console.error('Failed to fetch linked EF:', error);
        }
      }
    } catch (error) {
      toast.error('Failed to load custom mapping details');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    fetchMappingDetails();
  }, [fetchMappingDetails]);

  const handleApproval = async () => {
    if (!approvalDialog.action) return;

    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/custom-mappings/${mapping.id}/approve`, {
        action: approvalDialog.action,
        comment: comment || null
      });
      toast.success(`Custom mapping ${approvalDialog.action === 'approve' ? 'approved' : 'rejected'}`);
      setApprovalDialog({ open: false, action: null });
      setComment('');
      
      // Navigate back to pending approvals or refresh
      if (fromPending) {
        navigate('/pending-approvals');
      } else {
        fetchMappingDetails();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to process approval');
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '—';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const DetailRow = ({ label, value }) => (
    <div className="py-3 border-b border-gray-100 last:border-0">
      <dt className="text-sm text-muted-foreground mb-1">{label}</dt>
      <dd className="font-medium">{value ?? '—'}</dd>
    </div>
  );

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center py-24">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </DashboardLayout>
    );
  }

  if (!mapping) {
    return (
      <DashboardLayout>
        <div className="text-center py-24">
          <p className="text-muted-foreground">Custom mapping not found</p>
          <Button className="mt-4" onClick={() => navigate('/custom-mappings')}>
            Back to Custom Mappings
          </Button>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6 animate-fade-in" data-testid="custom-mapping-detail-page">
        {/* Header */}
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate(backUrl)}
              data-testid="back-btn"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <div className="flex items-center gap-3">
                <h1 className="font-heading text-3xl font-bold text-foreground">
                  Custom Mapping #{mapping.ef_training_set_id || mapping.id}
                </h1>
                <StatusBadge status={mapping.status} />
              </div>
              <p className="text-muted-foreground mt-1">
                {getCategoryLabel(mapping.category)} → EF #{mapping.desired_emission_factor_id}
              </p>
            </div>
          </div>
          
          {/* Action Buttons */}
          <div className="flex gap-2">
            {/* Edit Button - for admins/approvers (all mappings) or creators (pending/rejected) */}
            {(canApprove() || mapping.status === 'pending' || mapping.status === 'rejected') && (
              <Link to={`/custom-mappings/${mapping.id}/edit`}>
                <Button variant="outline" data-testid="edit-btn">
                  <Edit className="w-4 h-4 mr-2" />
                  {mapping.status === 'rejected' ? 'Edit & Resubmit' : 'Edit'}
                </Button>
              </Link>
            )}
            
            {/* Approval Buttons */}
            {canApprove() && mapping.status === 'pending' && (
              <>
                <Button
                  variant="outline"
                  className="text-red-600 border-red-200 hover:bg-red-50"
                  onClick={() => setApprovalDialog({ open: true, action: 'reject' })}
                  data-testid="reject-btn"
                >
                  <X className="w-4 h-4 mr-2" />
                  Reject
                </Button>
                <Button
                  className="bg-green-600 hover:bg-green-700"
                  onClick={() => setApprovalDialog({ open: true, action: 'approve' })}
                  data-testid="approve-btn"
                >
                  <Check className="w-4 h-4 mr-2" />
                  Approve
                </Button>
              </>
            )}
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="border-gray-200">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                <Link2 className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Linked EF ID</p>
                <p className="font-medium text-sm">#{mapping.desired_emission_factor_id}</p>
              </div>
            </CardContent>
          </Card>
          
          <Card className="border-gray-200">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
                <FileText className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Is Spend Based</p>
                <p className="font-medium text-sm">{mapping.is_spend_based ? 'Yes' : 'No'}</p>
              </div>
            </CardContent>
          </Card>
          
          <Card className="border-gray-200">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                <User className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Created By</p>
                <p className="font-medium text-sm truncate max-w-32" title={mapping.created_by}>
                  {mapping.created_by}
                </p>
              </div>
            </CardContent>
          </Card>
          
          <Card className="border-gray-200">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                <Calendar className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Created At</p>
                <p className="font-medium text-sm">{formatDate(mapping.created_at)}</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Rejection Comment Alert */}
        {mapping.status === 'rejected' && mapping.comment && (
          <Card className="border-red-200 bg-red-50">
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-lg bg-red-100 flex items-center justify-center flex-shrink-0">
                  <MessageSquare className="w-5 h-5 text-red-600" />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-red-800 mb-1">Rejection Reason</h3>
                  <p className="text-red-700">{mapping.comment}</p>
                  <p className="text-sm text-red-600 mt-2">
                    You can edit this mapping and resubmit it for approval.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Tabs */}
        <Tabs defaultValue="details" className="space-y-6">
          <TabsList>
            <TabsTrigger value="details" className="gap-2" data-testid="tab-details">
              <FileText className="w-4 h-4" />
              Details
            </TabsTrigger>
            <TabsTrigger value="linked-ef" className="gap-2" data-testid="tab-linked-ef">
              <Link2 className="w-4 h-4" />
              Linked EF Details
            </TabsTrigger>
            <TabsTrigger value="comments" className="gap-2" data-testid="tab-comments">
              <MessageSquare className="w-4 h-4" />
              Comments ({efHistory.filter(h => h.comment).length})
            </TabsTrigger>
          </TabsList>

          {/* Details Tab */}
          <TabsContent value="details" className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Custom Mapping Information */}
              <Card className="border-gray-200">
                <CardHeader>
                  <CardTitle className="font-heading text-lg">Custom Mapping Information</CardTitle>
                </CardHeader>
                <CardContent className="space-y-0">
                  <DetailRow label="Mapping ID" value={mapping.ef_training_set_id || mapping.id} />
                  <DetailRow label="Category" value={getCategoryLabel(mapping.category)} />
                  <DetailRow label="Sub Category" value={mapping.sub_category} />
                  <DetailRow label="Type" value={mapping.type} />
                  <DetailRow label="Sub Type" value={mapping.sub_type} />
                  <DetailRow label="Additional Information" value={mapping.additional_information} />
                  <DetailRow label="Is Spend Based" value={mapping.is_spend_based ? 'Yes' : 'No'} />
                </CardContent>
              </Card>

              {/* Linked Emission Factor Summary */}
              <Card className="border-gray-200">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="font-heading text-lg">Linked Emission Factor</CardTitle>
                    <Link to={`/emission-factors/${mapping.desired_emission_factor_id}`}>
                      <Button variant="outline" size="sm">
                        <ExternalLink className="w-4 h-4 mr-2" />
                        View Full EF
                      </Button>
                    </Link>
                  </div>
                </CardHeader>
                <CardContent className="space-y-0">
                  <DetailRow label="EF ID" value={`#${mapping.desired_emission_factor_id}`} />
                  <DetailRow label="Category" value={getCategoryLabel(linkedEF?.category)} />
                  <DetailRow label="Sub Category" value={linkedEF?.sub_category} />
                  <DetailRow label="Type" value={linkedEF?.type} />
                  <DetailRow label="Country" value={getCountryName(linkedEF?.country)} />
                  <DetailRow label="CO2e" value={linkedEF?.co2e} />
                </CardContent>
              </Card>

              {/* Submission Information */}
              <Card className="border-gray-200">
                <CardHeader>
                  <CardTitle className="font-heading text-lg">Submission Information</CardTitle>
                </CardHeader>
                <CardContent className="space-y-0">
                  <DetailRow label="Status" value={<StatusBadge status={mapping.status} />} />
                  <DetailRow label="Version" value={mapping.version} />
                  <DetailRow label="Created By" value={mapping.created_by} />
                  <DetailRow label="Created At" value={formatDate(mapping.created_at)} />
                  {mapping.reviewed_by && (
                    <>
                      <DetailRow label="Reviewed By" value={mapping.reviewed_by_name || mapping.reviewed_by} />
                    </>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Linked EF Details Tab */}
          <TabsContent value="linked-ef" className="space-y-6">
            {linkedEF ? (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* General Information */}
                <Card className="border-gray-200">
                  <CardHeader>
                    <CardTitle className="font-heading text-lg">General Information</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-0">
                    <DetailRow label="EF ID" value={linkedEF.emission_factor_id} />
                    <DetailRow label="Category" value={getCategoryLabel(linkedEF.category)} />
                    <DetailRow label="Sub Category" value={linkedEF.sub_category} />
                    <DetailRow label="Type" value={linkedEF.type} />
                    <DetailRow label="Sub Type" value={linkedEF.sub_type} />
                    <DetailRow label="Scope" value={linkedEF.scope} />
                    <DetailRow label="Description" value={linkedEF.description} />
                    <DetailRow label="Tag" value={linkedEF.tag} />
                  </CardContent>
                </Card>

                {/* Location Information */}
                <Card className="border-gray-200">
                  <CardHeader>
                    <CardTitle className="font-heading text-lg">Location</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-0">
                    <DetailRow label="Country" value={getCountryName(linkedEF.country)} />
                    <DetailRow label="Country Code" value={linkedEF.country} />
                    <DetailRow label="State" value={linkedEF.state} />
                    <DetailRow label="City" value={linkedEF.city} />
                    <DetailRow label="Site Identity" value={linkedEF.site_identity} />
                  </CardContent>
                </Card>

                {/* Emission Data */}
                <Card className="border-gray-200">
                  <CardHeader>
                    <CardTitle className="font-heading text-lg">Emission Data</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-0">
                    <DetailRow label="CO2e" value={linkedEF.co2e} />
                    <DetailRow label="CO2 Factor" value={linkedEF.co2factor} />
                    <DetailRow label="CH4 Factor" value={linkedEF.ch4factor} />
                    <DetailRow label="N2O Factor" value={linkedEF.n2o_factor} />
                    <DetailRow label="NO2 Factor" value={linkedEF.no2factor} />
                    <DetailRow label="CO4 Factor" value={linkedEF.co4factor} />
                    <DetailRow label="Biogenic CO2 Factor" value={linkedEF.biogenic_co2factor} />
                    <DetailRow label="Input Unit" value={linkedEF.input_unit} />
                    <DetailRow label="Unit Category" value={linkedEF.unit_category} />
                  </CardContent>
                </Card>

                {/* Source Information */}
                <Card className="border-gray-200">
                  <CardHeader>
                    <CardTitle className="font-heading text-lg">Source Information</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-0">
                    <DetailRow label="Source Name" value={linkedEF.source_name} />
                    <DetailRow label="Source Version" value={linkedEF.source_version} />
                    <DetailRow label="Source URL" value={linkedEF.sourceurl} />
                    <DetailRow label="AR Version" value={linkedEF.ar_version} />
                    <DetailRow label="Is Primary Factor" value={linkedEF.is_primary_factor ? 'Yes' : 'No'} />
                    <DetailRow label="Is Spend Based" value={linkedEF.is_spend_based ? 'Yes' : 'No'} />
                  </CardContent>
                </Card>

                {/* Water Data */}
                <Card className="border-gray-200">
                  <CardHeader>
                    <CardTitle className="font-heading text-lg">Water Data</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-0">
                    <DetailRow label="Water Use (m³)" value={linkedEF.water_use_m3} />
                    <DetailRow label="Water Data Source" value={linkedEF.water_data_source_name} />
                    <DetailRow label="Water Source Version" value={linkedEF.water_data_source_version} />
                    <DetailRow label="Water Source URL" value={linkedEF.water_data_sourceurl} />
                  </CardContent>
                </Card>

                {/* Flag Data */}
                <Card className="border-gray-200">
                  <CardHeader>
                    <CardTitle className="font-heading text-lg">Flag Data</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-0">
                    <DetailRow label="Flag CO2e" value={linkedEF.flag_co2e} />
                    <DetailRow label="Flag Source Name" value={linkedEF.flag_source_name} />
                    <DetailRow label="Flag Source Version" value={linkedEF.flag_source_version} />
                    <DetailRow label="Flag Source URL" value={linkedEF.flag_sourceurl} />
                  </CardContent>
                </Card>

                {/* Additional Information */}
                <Card className="border-gray-200 lg:col-span-2">
                  <CardHeader>
                    <CardTitle className="font-heading text-lg">Additional Information</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground">
                      {linkedEF.additional_information || 'No additional information provided.'}
                    </p>
                  </CardContent>
                </Card>
              </div>
            ) : (
              <div className="text-center py-12 text-muted-foreground">
                <FileText className="w-12 h-12 mx-auto mb-3 opacity-30" />
                <p>Unable to load linked emission factor details</p>
                <p className="text-sm">EF ID: #{mapping.desired_emission_factor_id}</p>
              </div>
            )}
          </TabsContent>

          {/* Comments Tab */}
          <TabsContent value="comments">
            <Card className="border-gray-200">
              <CardHeader>
                <CardTitle className="font-heading text-lg">Comments</CardTitle>
                <CardDescription>
                  All comments and feedback on the linked emission factor
                </CardDescription>
              </CardHeader>
              <CardContent>
                {efHistory.filter(h => h.comment).length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <MessageSquare className="w-12 h-12 mx-auto mb-3 opacity-30" />
                    <p>No comments yet</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {efHistory.filter(h => h.comment).map((entry, index) => (
                      <div 
                        key={entry.id || index}
                        className={`p-4 border rounded-lg ${
                          entry.action === 'reject' ? 'border-red-200 bg-red-50' :
                          entry.action === 'approve' ? 'border-green-200 bg-green-50' :
                          'border-blue-200 bg-blue-50'
                        }`}
                        data-testid={`comment-entry-${index}`}
                      >
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-3">
                            <span className={`px-2 py-1 rounded text-xs font-medium ${
                              entry.action === 'approve' ? 'bg-green-100 text-green-800' :
                              entry.action === 'reject' ? 'bg-red-100 text-red-800' :
                              'bg-blue-100 text-blue-800'
                            }`}>
                              {entry.action === 'reject' ? 'Rejection Feedback' : 
                               entry.action === 'approve' ? 'Approval Note' : 
                               'Comment'}
                            </span>
                          </div>
                          <span className="text-sm text-muted-foreground">
                            {formatDate(entry.created_at)}
                          </span>
                        </div>
                        
                        <p className="text-sm mb-3">{entry.comment}</p>
                        
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <User className="w-4 h-4" />
                          <span className="font-medium">{entry.user_name}</span>
                          <span>({entry.user_email})</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Approval Dialog */}
        <Dialog open={approvalDialog.open} onOpenChange={(open) => { setApprovalDialog({ ...approvalDialog, open }); setComment(''); }}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>
                {approvalDialog.action === 'approve' ? 'Approve' : 'Reject'} Custom Mapping
              </DialogTitle>
              <DialogDescription>
                {approvalDialog.action === 'approve' 
                  ? 'This will add the mapping to the training set and make it available for use.'
                  : 'Please provide a reason for rejecting this mapping.'}
              </DialogDescription>
            </DialogHeader>

            <div className="py-4 space-y-2 text-sm border rounded-lg p-4 bg-gray-50">
              <p><strong>Custom Category:</strong> {getCategoryLabel(mapping.category)}</p>
              <p><strong>Custom Sub Category:</strong> {mapping.sub_category}</p>
              <p><strong>Custom Type:</strong> {mapping.type || '—'}</p>
              <p><strong>Custom Sub Type:</strong> {mapping.sub_type || '—'}</p>
              <p><strong>Additional Information:</strong> {mapping.additional_information || '—'}</p>
              <p><strong>Linked EF:</strong> #{mapping.desired_emission_factor_id}</p>
              <p><strong>Created By:</strong> {mapping.created_by}</p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="comment">
                Comment {approvalDialog.action === 'reject' && <span className="text-red-500">*</span>}
              </Label>
              <Textarea
                id="comment"
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder={approvalDialog.action === 'reject' ? 'Please provide reason for rejection' : 'Optional comment'}
                rows={3}
                data-testid="approval-comment"
              />
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => { setApprovalDialog({ open: false, action: null }); setComment(''); }}>
                Cancel
              </Button>
              <Button
                onClick={handleApproval}
                disabled={submitting || (approvalDialog.action === 'reject' && !comment)}
                className={approvalDialog.action === 'approve' ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'}
                data-testid="confirm-approval-btn"
              >
                {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                {approvalDialog.action === 'approve' ? 'Approve' : 'Reject'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
